import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SaveIndicator — subtle Google-Docs-style save status badge.
 *
 * Props:
 *   st         – { state: "idle"|"pending"|"saving"|"saved"|"error", at?: Date }
 *   className  – extra Tailwind classes (optional)
 *   theme      – "light" (default, office white-bg forms) | "dark" (worker dark-bg shell)
 *
 * States:
 *   pending / saving  → spinner + "Saving…"         (muted)
 *   saved             → tick   + "Saved HH:MM"       (green)
 *   error             → warning + "Couldn't save — click Save"  (red/amber)
 *   idle / null       → renders nothing
 */
const fmtClock = (d) => {
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const THEME = {
  light: {
    saving: "text-slate-400",
    saved:  "text-emerald-600",
    error:  "text-rose-500",
  },
  dark: {
    saving: "text-slate-400",
    saved:  "text-emerald-300",
    error:  "text-red-300",
  },
};

export function SaveIndicator({ st, className, theme = "light" }) {
  if (!st || st.state === "idle") return null;

  const t = THEME[theme] || THEME.light;

  const map = {
    pending: { txt: "Saving…",                      cls: t.saving, spin: true },
    saving:  { txt: "Saving…",                      cls: t.saving, spin: true },
    saved:   { txt: `Saved ${fmtClock(st.at)}`,     cls: t.saved,  Icon: CheckCircle2 },
    error:   { txt: "Couldn't save — click Save",   cls: t.error,  Icon: AlertTriangle },
  };

  const c = map[st.state];
  if (!c) return null;
  const { Icon } = c;

  return (
    <span
      className={cn("text-[11px] flex items-center gap-1 select-none", c.cls, className)}
      data-testid="save-indicator"
    >
      {c.spin
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : Icon
          ? <Icon className="h-3 w-3" />
          : null}
      {c.txt}
    </span>
  );
}
